/**
 * Standup surfaces helpers UI (WP-C): focus composer (project chips /
 * autocomplete), project delta cards, and a thin New-project stub sheet
 * (WP-D replaces with full epic create).
 * The orphan-Jira triage + stale-projects panels were removed 2026-08-06 —
 * the Projects tab is version hero → focus cards → the shared projects table.
 */
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { brainTokens } from "../../design/brainTheme";
import { EmptyState, SectionCard, StatusPill } from "../../mui";
import {
  getOrphanJiraIssues,
  assignOrphanToProject,
  listProjects,
  getStaleProjects,
  saveProject,
  type ProjectJiraIssueRow,
  type Project,
  type ProjectKind,
} from "../../lib/brain-api";
import type { PersonProjectDelta, StandupPersonDeltas } from "../../lib/api";

const c = brainTokens.color;

// ---------------------------------------------------------------------------
// WP-C — project delta cards (replace MOST IMPORTANT ticket cards)
// ---------------------------------------------------------------------------

/** Health tone for a project card's left bar + progress fill. */
function projectTone(delta: PersonProjectDelta | undefined, project: Project | undefined) {
  const status = project?.status;
  if ((delta?.newBlockers ?? 0) > 0 || project?.blocked) {
    return { bar: c.dangerDot, track: c.dangerDot, label: "Blocked", pill: { bg: c.dangerBg, fg: c.dangerText } };
  }
  if (delta?.planAwaiting || status === "planning" || status === "draft") {
    return { bar: c.warnDot, track: c.warnDot, label: "Watch", pill: { bg: c.warnBg, fg: c.warnText } };
  }
  return { bar: c.successDot, track: c.successDot, label: "On track", pill: { bg: c.successBg, fg: c.successText } };
}

/**
 * The person's projects as rich cards (3-up), reusing the `sn-tcard` look:
 * coloured left bar by health, a progress track, movement signals, and a
 * "Focus" action that populates the focus composer with that project.
 */
export function ProjectDeltaCards({
  projects,
  deltas,
  loading,
  focusProjectId,
  onFocus,
}: {
  projects: { projectId: string; name: string; primary?: boolean }[];
  deltas: PersonProjectDelta[];
  loading?: boolean;
  focusProjectId?: string;
  onFocus: (projectId: string) => void;
}) {
  // Enrich with the registry record for status/progress/owner. ponytail: one
  // extra list call per person tab; fine at team scale, cache if it ever bites.
  const [records, setRecords] = useState<Record<string, Project>>({});
  useEffect(() => {
    let cancelled = false;
    listProjects()
      .then((ps) => {
        if (cancelled) return;
        setRecords(Object.fromEntries(ps.map((p) => [p.projectId, p])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box sx={{ py: 1 }}>
        <CircularProgress size={18} sx={{ color: c.teal }} />
      </Box>
    );
  }
  if (!projects.length) return null;

  const deltaById = new Map(deltas.map((d) => [d.projectId, d]));

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box className="sn-grp" sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: c.teal }} />
        <Box sx={{ fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: c.faint }}>
          Projects
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" },
          gap: 1.5,
          mb: 1.5,
        }}
      >
        {projects.map((p) => {
          const d = deltaById.get(p.projectId);
          const rec = records[p.projectId];
          const tone = projectTone(d, rec);
          const pct = Math.max(0, Math.min(100, Math.round(rec?.completeness ?? 0)));
          const focusing = focusProjectId === p.projectId;
          const movement = d?.delta && d.delta.trim() ? d.delta : "No movement since yesterday";
          return (
            <div key={p.projectId} className="sn-tcard" style={{ minHeight: 168, borderLeftColor: tone.bar }}>
              <div className="sn-tcard-top">
                <span className="sn-tcard-status" style={{ background: tone.pill.bg, color: tone.pill.fg }}>
                  <span style={{ width: 5, height: 5, borderRadius: "50%", background: tone.bar }} />
                  {tone.label}
                </span>
                <button
                  type="button"
                  onClick={() => onFocus(p.projectId)}
                  aria-pressed={focusing}
                  title={focusing ? "This project is set on your focus composer" : "Add a focus on this project"}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "4px 10px",
                    borderRadius: 20,
                    border: `1px solid ${focusing ? "#0f8f8f" : "#d6e3e1"}`,
                    background: focusing ? "#0f8f8f" : "#fff",
                    color: focusing ? "#fff" : "#2c5a5a",
                    fontFamily: "inherit",
                    fontSize: 11.5,
                    fontWeight: 600,
                    cursor: "pointer",
                    transition: "background .12s, border-color .12s",
                  }}
                >
                  {focusing ? "✓ Focus" : "+ Focus"}
                </button>
              </div>
              <RouterLink
                to={`/projects/${encodeURIComponent(p.projectId)}`}
                className="sn-tcard-title"
                style={{ textDecoration: "none", WebkitLineClamp: 2, marginBottom: 4 }}
              >
                {p.name}
              </RouterLink>
              <div style={{ marginTop: "auto", paddingTop: 12 }}>
                <div className="sn-tcard-barlbl">
                  <span>Progress</span>
                  <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: "#8b9693" }}>{pct}%</span>
                </div>
                <div className="sn-tcard-track">
                  <div style={{ width: `${pct}%`, height: "100%", borderRadius: 3, background: tone.track }} />
                </div>
              </div>
              <div className="sn-tcard-foot" style={{ marginTop: 12 }}>
                <span style={{ fontSize: 11.5, color: "#7c8a87", lineHeight: 1.35, flex: 1 }}>{movement}</span>
                {(d?.doneSince ?? 0) > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#2f6a51" }} title="Done since yesterday">↑{d!.doneSince}</span>
                )}
                {(d?.newBlockers ?? 0) > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#a13a40" }} title="New blockers">⚠{d!.newBlockers}</span>
                )}
              </div>
            </div>
          );
        })}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// WP-C — focus composer (decision 25: A1/A2/A4 + issue link + amber unlinked)
// ---------------------------------------------------------------------------

export type FocusLinkDraft = {
  text: string;
  projectId?: string;
};

export function FocusComposer({
  canEdit,
  person,
  onAdd,
  projectId,
  onProjectChange,
}: {
  canEdit: boolean;
  person: StandupPersonDeltas | null;
  onAdd: (draft: FocusLinkDraft) => void;
  /** Selected project, controlled by the parent (also set from a project card's Focus button). */
  projectId?: string;
  onProjectChange: (id: string | undefined) => void;
}) {
  const [text, setText] = useState("");
  const [boundUserId, setBoundUserId] = useState<string | undefined>(undefined);

  // Only the person's own projects — the ones they own or have work on.
  const myProjects = person?.projects ?? [];

  // Reset the draft when switching people — never wipe mid-type as deltas arrive.
  useEffect(() => {
    const uid = person?.userId;
    if (!uid || uid === boundUserId) return;
    setBoundUserId(uid);
    setText("");
  }, [person?.userId, boundUserId]);

  if (!canEdit) return null;

  const submit = () => {
    const t = text.trim();
    if (!t) return;
    onAdd({
      text: t,
      ...(projectId ? { projectId } : {}),
    });
    setText("");
    // Keep the project selection for the next focus (same project, multiple focuses).
  };

  return (
    <Box sx={{ mt: 1.25 }}>
      <Box className="sn-addrow" sx={{ alignItems: "center", flexWrap: "wrap", gap: 1 }}>
        <div className="sn-addbox">+</div>
        <input
          className="sn-addtext"
          value={text}
          placeholder="Add a focus…"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          style={{ flex: 1, minWidth: 140 }}
        />
        <select
          className="sn-select"
          value={projectId ?? ""}
          onChange={(e) => onProjectChange(e.target.value || undefined)}
          aria-label="Link to a project"
          style={{ maxWidth: 180 }}
        >
          <option value="">No project</option>
          {myProjects.map((p) => (
            <option key={p.projectId} value={p.projectId}>
              {p.name}
            </option>
          ))}
        </select>
        <button className="sn-addbtn" onClick={submit} disabled={!text.trim()} type="button">
          Add
        </button>
      </Box>
    </Box>
  );
}

/** Quiet amber dot for an unlinked focus (never a blocker). */
export function FocusLinkDot({ linked }: { linked: boolean }) {
  return (
    <Box
      title={linked ? "Linked to a project" : "Unlinked focus"}
      aria-label={linked ? "Linked to a project" : "Unlinked focus"}
      sx={{
        width: 7,
        height: 7,
        borderRadius: "50%",
        flex: "none",
        bgcolor: linked ? c.teal : c.warnDot,
        opacity: linked ? 0.85 : 0.55,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// New project — inline create (upsert). Opens straight from the header button;
// starts a draft and jumps to the project page to flesh it out.
// ---------------------------------------------------------------------------

/** name → url-safe project id (deduped by the registry, so a suffix avoids clobber). */
function slugify(name: string, taken: Set<string>): string {
  const base =
    name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "project";
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) {
    const candidate = `${base}-${i}`;
    if (!taken.has(candidate)) return candidate;
  }
}

export function NewProjectStubSheet({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<ProjectKind>("internal");
  const [goal, setGoal] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName("");
    setKind("internal");
    setGoal("");
    setSaving(false);
  };
  const close = () => {
    reset();
    onClose();
  };

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      const existing = await listProjects().catch(() => [] as Project[]);
      const projectId = slugify(trimmed, new Set(existing.map((p) => p.projectId)));
      await saveProject({
        projectId,
        name: trimmed,
        kind,
        status: "draft",
        ...(goal.trim() ? { description: goal.trim() } : {}),
      });
      enqueueSnackbar(`Created “${trimmed}”`, { variant: "success" });
      close();
      navigate(`/projects/${encodeURIComponent(projectId)}`);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Could not create the project.", { variant: "error" });
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle>New project</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") void create();
            }}
            autoFocus
            fullWidth
            size="small"
          />
          <TextField
            select
            label="Type"
            value={kind}
            onChange={(e) => setKind(e.target.value as ProjectKind)}
            fullWidth
            size="small"
          >
            <MenuItem value="internal">Internal</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
          </TextField>
          <TextField
            label="Goal (optional)"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="One line — what does done look like?"
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={() => void create()} disabled={!name.trim() || saving}>
          {saving ? "Creating…" : "Create project"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
